import React from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { EditorMultiSignUpSettings } from '@dev/template-editor';
import { FormPageTypeEnum, FormSettingsEnum } from '@dev/ee-api/ee-api-models';
import { getGeneralInfo } from '@/store/account/actions';
import { TemplateFormErrorNames } from '@/store/landingPageCreator/types';
import { getLandingPageCreatorStore } from '@/store/landingPageCreator/actions';
import { FormMultiSettingsAudience } from '../components/FormMultiSettingsAudience';
import { FormMultiSettingsSubscription } from '../components/FormMultiSettingsSubscription';
import { useContactListsNamesMap } from './useContactLists';
import { FormMultiSettingsNotifications } from '../components/FormMultiSettingsNotifications';


const isSubscriptionSectionError = (templateErrors?: Partial<Record<TemplateFormErrorNames, boolean>>): boolean =>
    templateErrors?.[TemplateFormErrorNames.FORM_SUBMIT_NONE_LP] || 
    templateErrors?.[TemplateFormErrorNames.ALREADY_SUBSCRIBED_NONE_LP] || 
    templateErrors?.[TemplateFormErrorNames.FORM_SUBMIT_INVALID_URL] ||
    templateErrors?.[TemplateFormErrorNames.ALREADY_SUBSCRIBED_INVALID_URL];

const isNotificationsSectionError = (templateErrors?: Partial<Record<TemplateFormErrorNames, boolean>>): boolean =>
    templateErrors?.[TemplateFormErrorNames.NOTIFY_INVALID_EMAILS] || 
    templateErrors?.[TemplateFormErrorNames.NOTIFY_EMAILS_NO_PROPER_SEPARATOR] || 
    templateErrors?.[TemplateFormErrorNames.NOTIFY_EMAILS_NUMBER_EXCEEDED] ||
    templateErrors?.[TemplateFormErrorNames.NOTIFY_EMAILS_NOT_ON_CONTACT_LIST];

interface MultiSignUpSettingsProps {
    selectedBlockIdRef: React.MutableRefObject<string>
}

export const getMultiSignUpPublicFormIdDefaultValue = (blockClientId: string) => `publicformid-value-${blockClientId}`;

export const useMultiSignUpSettings = ({ 
    selectedBlockIdRef 
}: MultiSignUpSettingsProps): EditorMultiSignUpSettings => {
    const { t } = useTranslation();
    const { PublicAccountID } = useSelector(getGeneralInfo);
    const { template } = useSelector(getLandingPageCreatorStore);
    const contactListsNames = useContactListsNamesMap();

    const multiSignUpSettingsForms = template.forms?.reduce((settings, templateForm) => ({
        ...settings,
        [templateForm.blockClientId]: {
            actionUrl: `${APIPATH}/v4/contact/form`,
            hiddenInputs: {
                publicaccountid: PublicAccountID,
                publicformid: templateForm?.form?.PublicFormID ?? getMultiSignUpPublicFormIdDefaultValue(templateForm.blockClientId),
            },
            settingsSections: [
                {
                    id: 'audience',
                    title: 'Audience',
                    content: <FormMultiSettingsAudience blockClientId={templateForm.blockClientId} selectedBlockClientIdRef={selectedBlockIdRef} />,
                    isExpanded: true
                },
                {
                    id: 'subscription',
                    title: t('subscription'),
                    content: <FormMultiSettingsSubscription blockClientId={templateForm.blockClientId} selectedBlockClientIdRef={selectedBlockIdRef} />,
                    isExpanded: isSubscriptionSectionError(templateForm.errors)
                },
                {
                    id: 'notifications',
                    title: t('notifications'),
                    content: <FormMultiSettingsNotifications blockClientId={templateForm.blockClientId} selectedBlockClientIdRef={selectedBlockIdRef} />,
                    isExpanded: isNotificationsSectionError(templateForm.errors)
                }
            ],
            listsLabels: templateForm.formPayload?.FormSettings?.includes(FormSettingsEnum.MailingPreference) && templateForm.formPayload?.Audience?.map((audience) => contactListsNames.get(audience)),
            mailingPreferencesLabel: templateForm.formMailingPreferencesLabel ?? t('selectyoursubscriptions'),
            successMessage: (templateForm.formPayload || templateForm.form)?.FormPageType?.includes(FormPageTypeEnum.ContactUploadSubmitMessage) ? t('thankyouforyoursubscription') : null,
            failureMessage: (templateForm.formPayload || templateForm.form)?.FormPageType?.includes(FormPageTypeEnum.ContactUploadSubscribedMessage) ? t('thisemailisalreadysubscribed') : null,
        }
    }), {});
    return {
        default: {
            actionUrl: `${APIPATH}/v4/contact/form`,
            settingsSections: [
                {
                    id: 'audience',
                    title: 'Audience',
                    content: <FormMultiSettingsAudience selectedBlockClientIdRef={selectedBlockIdRef} />,
                    isExpanded: true
                }, 
                {
                    id: 'subscription',
                    title: t('subscription'),
                    content: <FormMultiSettingsSubscription selectedBlockClientIdRef={selectedBlockIdRef} />,
                    isExpanded: false
                },
                {
                    id: 'notifications',
                    title: t('notifications'),
                    content: <FormMultiSettingsNotifications selectedBlockClientIdRef={selectedBlockIdRef} />,
                    isExpanded: false
                }
            ]
        },
        ...multiSignUpSettingsForms
    }
};
